import {
  ACTIVE_SEGMENT_KIND,
  applyDurationDrag,
  formatBoardMinutes,
  formatDuration,
  rawBoardMinutes,
  type DragHandle,
  type PreviewLayout,
  type PreviewSegment,
  type SegmentHandle,
} from '../../../shared/lib/schedule';
import { trueDurations } from './buildSchedulePatch';
import type { BoardBlock } from './boardTypes';

/**
 * Where a drag would land, **with its Anfahrt / Arbeit / Abfahrt split** — the model behind the drag
 * ghost. Pure, no React.
 *
 * A drag used to preview as one flat bar, so resizing the Anfahrt looked exactly like moving the
 * whole Termin. The split is derived from `applyDurationDrag`, the same function
 * `buildSchedulePatch` commits through, so the ghost cannot show a shape the request will not
 * produce.
 *
 * Geometry is deliberately **unclamped**: a block clipped at 06:00 has a `span.startMin` of 0, which
 * is the window edge and not where its Anfahrt really begins. The ghost is cut to the window when it
 * is drawn instead, so it stays aligned with the block it came from.
 */

export function buildPreviewLayout(
  block: BoardBlock,
  dayIso: string,
  handle: DragHandle,
  deltaMinutes: number,
): PreviewLayout {
  const { entry, span } = block;

  const startMin = rawBoardMinutes(entry.startsAt, dayIso);
  const endMin = rawBoardMinutes(entry.endsAt, dayIso);
  // `toBoardSpan` already rejected an unparseable entry, so this is belt-and-braces.
  const safeStart = Number.isNaN(startMin) ? span.startMin : startMin;
  const safeEnd = Number.isNaN(endMin) ? span.endMin : endMin;

  const colorOf = (kind: PreviewSegment['kind']) =>
    block.segments.find((segment) => segment.kind === kind)?.color ?? '#94A3B8';

  // An absence or a break is one solid bar — no travel, no departure. Mirrors the ABSENCE branch of
  // `buildSchedulePatch`: `start` moves the start alone, `end` the end alone, `move` both.
  if (entry.type === 'ABSENCE' || entry.type === 'BREAK') {
    const start = handle === 'end' ? safeStart : safeStart + deltaMinutes;
    const end = handle === 'start' ? safeEnd : safeEnd + deltaMinutes;

    return {
      startMin: start,
      endMin: end,
      segments: [
        {
          kind: 'work',
          minutes: Math.max(0, end - start),
          color: colorOf('work'),
          label: handle === 'move' ? formatBoardMinutes(start) : formatDuration(end - start),
          active: true,
        },
      ],
    };
  }

  const { workStartsAt, ...base } = trueDurations(entry);
  const { durations, startDelta } = applyDurationDrag(
    base,
    handle as 'move' | SegmentHandle,
    deltaMinutes,
  );

  const start = safeStart + startDelta;
  const total = durations.travel + durations.work + durations.departure;
  const activeKind = ACTIVE_SEGMENT_KIND[handle];

  const label = (kind: PreviewSegment['kind'], minutes: number) =>
    // A move changes no duration, so three unchanged minute counts would say nothing. The work strip
    // carries the new start time instead — the one number a move actually changes.
    handle === 'move' && kind === 'work'
      ? formatBoardMinutes(start + durations.travel)
      : formatDuration(minutes);

  return {
    startMin: start,
    endMin: start + total,
    segments: (['travel', 'work', 'departure'] as const).map((kind) => ({
      kind,
      minutes: durations[kind],
      color: colorOf(kind),
      label: label(kind, durations[kind]),
      active: activeKind == null || activeKind === kind,
    })),
  };
}
